/**
 * Emits Rybbit funnel events as the reactour tour advances, so we can see how
 * far prospects get through the demo tour.
 *
 * Why Rybbit and not the homegrown metrics: `lib/metrics.ts` `track()` bails
 * when there's no userId, so it never sees anonymous prospects — exactly the
 * people taking the tour before signing up. Rybbit tracks sessions/events
 * without a login, so the tour funnel lives here.
 *
 * Events (define a funnel over these in the Rybbit dashboard):
 *   tour_started    { variant, steps }
 *   tour_step       { variant, step, steps, target }   — one per step reached
 *   tour_completed  { variant, step, steps }           — closed on the last step
 *   tour_abandoned  { variant, step, steps }           — closed before the end
 *
 * Mounted under TourProvider in both orientations (landscape passes the
 * selected variant, portrait passes "mobile"). Never renders anything.
 * @module TourAnalyticsBridge
 * @category Functional Components
 */
import type { StepType } from "@reactour/tour";
import { useTour } from "@reactour/tour";
import { useEffect, useRef } from "react";
import { emitRybbitEvent } from "../rybbit";

// emitRybbitEvent waits for the async rybbit.init() to resolve before sending,
// so the leading tour events (tour_started, first tour_step) on a cold demo
// start are queued rather than silently dropped.
function emit(name: string, props: Record<string, string | number | boolean>) {
	emitRybbitEvent(name, props);
}

export function TourAnalyticsBridge({ variant }: { variant: string }) {
	const { isOpen, currentStep, steps } = useTour();
	const total = steps?.length ?? 0;
	const wasOpen = useRef(false);
	const reached = useRef<number | null>(null);

	// ai-first attaches a per-step section label (see SectionedSteps) so we can
	// report the 5 phases, not the ~34 auto-advancing micro-steps. Absent on
	// other variants — then events simply carry no section.
	const sectionAt = (i: number): string | undefined =>
		(steps as StepType[] & { __sections?: string[] })?.__sections?.[i];

	// open / close transitions
	useEffect(() => {
		if (isOpen && !wasOpen.current) {
			wasOpen.current = true;
			reached.current = null;
			emit("tour_started", { variant, steps: total });
		} else if (!isOpen && wasOpen.current) {
			wasOpen.current = false;
			const at = reached.current ?? 0;
			const done = total > 0 && at >= total - 1;
			const section = sectionAt(at);
			emit(done ? "tour_completed" : "tour_abandoned", {
				variant,
				step: at,
				steps: total,
				...(section ? { section } : {}),
			});
		}
	}, [isOpen, total, variant]);

	// per-step progression while open (fires for the first step too)
	useEffect(() => {
		if (!isOpen || reached.current === currentStep) return;
		reached.current = currentStep;
		const selector = steps?.[currentStep]?.selector;
		const section = sectionAt(currentStep);
		emit("tour_step", {
			variant,
			step: currentStep,
			steps: total,
			target: typeof selector === "string" ? selector : "",
			...(section ? { section } : {}),
		});
	}, [isOpen, currentStep, steps, total, variant]);

	return null;
}
